import { useState, useEffect } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { useLocations, useCurrentUser, useCreateLocation, useUpdateLocation, useDeleteLocation } from '@/hooks/useQueries';

interface Location {
  id: number;
  name: string;
  code: string;
  type?: 'receiving' | 'issuing';
}

export default function Locations() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<Location | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);

  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const { data: locationsData, isLoading: locationsLoading, error: locationsError } = useLocations({
    per_page: 100,
  });

  const createLocationMutation = useCreateLocation();
  const updateLocationMutation = useUpdateLocation();
  const deleteLocationMutation = useDeleteLocation();

  const [locations, setLocations] = useState<Location[]>([]);
  const loading = locationsLoading || userLoading;
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: 'issuing' as 'receiving' | 'issuing',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (locationsError) {
      const errorMessage = locationsError instanceof Error
        ? locationsError.message
        : 'Failed to fetch locations. Please try again.';
      setError(errorMessage);
      setLocations([]);
      return;
    }

    if (locationsData !== undefined && !locationsLoading) {
      let apiLocations: any[] = [];

      if (Array.isArray(locationsData)) {
        apiLocations = locationsData;
      } else if (locationsData?.data && Array.isArray(locationsData.data)) {
        apiLocations = locationsData.data;
      }

      const transformedLocations: Location[] = apiLocations.map((loc: any) => ({
        id: loc.id,
        name: loc.name || '',
        code: loc.code || '',
        type: loc.type || 'issuing',
      }));

      setLocations(transformedLocations);
    }
  }, [locationsData, locationsLoading, locationsError]);

  const handleAdd = () => {
    setFormData({ name: '', code: '', type: 'issuing' });
    setError('');
    setSuccess('');
    setShowAddModal(true);
  };

  const handleEdit = (location: Location) => {
    setSelectedLocation(location);
    setFormData({ name: location.name, code: location.code, type: location.type || 'issuing' });
    setError('');
    setSuccess('');
    setShowEditModal(true);
  };

  const handleDelete = (location: Location) => {
    setLocationToDelete(location);
    setShowDeleteModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      if (showAddModal) {
        await createLocationMutation.mutateAsync({
          name: formData.name.trim(),
          code: formData.code.trim().toUpperCase(),
          type: formData.type,
        });
        setSuccess('Location created successfully');
        setShowAddModal(false);
      } else if (showEditModal && selectedLocation) {
        await updateLocationMutation.mutateAsync({
          id: selectedLocation.id,
          data: {
            name: formData.name.trim(),
            code: formData.code.trim().toUpperCase(),
            type: formData.type,
          },
        });
        setSuccess('Location updated successfully');
        setShowEditModal(false);
        setSelectedLocation(null);
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save location');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!locationToDelete) return;
    setDeleting(true);

    try {
      await deleteLocationMutation.mutateAsync(locationToDelete.id);
      setShowDeleteModal(false);
      setLocationToDelete(null);
      setSuccess('Location deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setShowDeleteModal(false);
      setLocationToDelete(null);
      setError(err.message || 'Failed to delete location. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setDeleting(false);
    }
  };

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <i className="ri-error-warning-line text-6xl text-gray-300 mb-3"></i>
          <p className="text-sm text-gray-500">You do not have permission to access this page.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Locations</h1>
          <p className="text-sm text-gray-600 mt-1">Manage storage locations</p>
        </div>

        {/* Toast — error */}
        {error && (
          <div className="fixed top-4 right-4 z-50 min-w-[300px] max-w-md shadow-lg flex rounded-sm overflow-hidden border border-red-200">
            <div className="w-1 bg-red-500 flex-shrink-0" />
            <div className="flex items-start gap-2.5 flex-1 bg-red-50 px-3 py-2.5">
              <i className="ri-error-warning-fill text-red-500 text-sm mt-0.5 flex-shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-red-900">Error</p>
                <p className="text-xs text-red-700 mt-0.5">{error}</p>
              </div>
              <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 flex-shrink-0">
                <i className="ri-close-line text-sm" />
              </button>
            </div>
          </div>
        )}

        {/* Toast — success */}
        {success && (
          <div className="fixed top-4 right-4 z-50 min-w-[300px] max-w-md shadow-lg flex rounded-sm overflow-hidden border border-emerald-200">
            <div className="w-1 bg-emerald-500 flex-shrink-0" />
            <div className="flex items-start gap-2.5 flex-1 bg-emerald-50 px-3 py-2.5">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-sm mt-0.5 flex-shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-emerald-900">Success</p>
                <p className="text-xs text-emerald-700 mt-0.5">{success}</p>
              </div>
              <button onClick={() => setSuccess('')} className="text-emerald-400 hover:text-emerald-600 flex-shrink-0">
                <i className="ri-close-line text-sm" />
              </button>
            </div>
          </div>
        )}

        {/* Locations Table */}
        <div className="bg-white rounded border border-gray-200 overflow-hidden max-w-2xl">
          {loading ? (
            <div className="text-center py-12">
              <span className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#50b3e1] inline-block"></span>
              <p className="text-sm text-gray-500 mt-2">Loading locations...</p>
            </div>
          ) : locations.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-map-pin-line text-6xl text-gray-300 mb-3"></i>
              <p className="text-sm text-gray-500">No locations found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="pl-4 pr-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Code</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Type</th>
                    <th className="px-3 pr-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {locations.map((location) => (
                    <tr key={location.id} className="hover:bg-gray-50">
                      <td className="pl-4 pr-3 py-4">
                        <div className="text-sm font-medium text-gray-900">{location.name}</div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className="px-2 py-0.5 bg-[#50b3e1]/10 text-[#50b3e1] rounded-full text-xs font-medium">
                          {location.code}
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          location.type === 'receiving' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {location.type === 'receiving' ? 'Receiving' : 'Issuing location'}
                        </span>
                      </td>
                      <td className="px-3 pr-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleEdit(location)}
                            className="text-[#50b3e1] hover:text-[#0997a4] transition-colors p-0.5"
                            title="Edit"
                          >
                            <i className="ri-edit-line text-base"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(location)}
                            className="text-[#ed1438] hover:text-[#c01030] transition-colors p-0.5"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line text-base"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add Location Button */}
        <div className="max-w-2xl">
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-[#50b3e1] text-white rounded text-sm font-medium hover:bg-[#0997a4] transition-colors cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            Add Location
          </button>
        </div>

        {/* Add / Edit Modal */}
        {(showAddModal || showEditModal) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {showAddModal ? 'Add Location' : 'Edit Location'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setSelectedLocation(null);
                    setError('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name <span className="text-[#ed1438]">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter location name"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Code <span className="text-[#ed1438]">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="Enter location code"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1] font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">Unique identifier (uppercase, no spaces)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type <span className="text-[#ed1438]">*</span>
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as 'receiving' | 'issuing' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                  >
                    <option value="issuing">Issuing location</option>
                    <option value="receiving">Receiving location</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">* Receiving locations can receive stock from suppliers.</p>
                  <p className="text-xs text-gray-500">* Issuing locations can issue stock to patients or other locations.</p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setSelectedLocation(null);
                      setError('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-[#50b3e1] text-white rounded-lg text-sm font-medium hover:bg-[#0997a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Saving...' : showAddModal ? 'Create' : 'Update'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && locationToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <i className="ri-error-warning-line text-4xl text-red-500"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Location</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Are you sure you want to delete <strong>{locationToDelete.name}</strong>?
                    This action cannot be undone. Locations with associated items or transactions cannot be deleted.
                  </p>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        setLocationToDelete(null);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmDelete}
                      disabled={deleting}
                      className="flex-1 px-4 py-2 bg-[#ed1438] text-white rounded-lg text-sm font-medium hover:bg-[#c01030] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleting ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
